import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight, GraduationCap, BookOpen, Sparkles, Zap, Trophy, Star, ChevronDown, Play, FileText, Users } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { useAuthDialog } from "@/lib/AuthDialogContext";
import { api } from "@/lib/api";

export default function Landing() {
  const { user } = useAuth();
  const { open: openAuth } = useAuthDialog();
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [faq, setFaq] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    api.get("/testimonials").then(r => setTestimonials(r.data)).catch(() => {});
    api.get("/faq").then(r => setFaq(r.data)).catch(() => {});
  }, []);

  const signIn = () => openAuth("login");

  const startAs = (role) => {
    sessionStorage.setItem("luminon_preferred_role", role);
    if (user) {
      if (!user.role) navigate("/onboarding");
      else navigate(user.role === "student" ? "/student/dashboard" : "/teacher/dashboard");
    } else openAuth("register");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar onSignIn={signIn} />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass mb-8" data-testid="hero-badge">
              <Sparkles className="w-3.5 h-3.5 text-secondary" />
              <span className="text-xs font-semibold tracking-widest uppercase">The future of learning</span>
            </div>
            <h1 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tighter">
              The Future of<br />
              <span className="bg-gradient-to-r from-indigo-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent">
                Learning
              </span>{" "}
              Begins Here.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              A premium learning ecosystem where chapter-organized notes, immersive video lessons,
              and interactive quizzes come together in one beautifully crafted experience.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="lg:col-span-12 mt-8 grid grid-cols-1 max-w-2xl gap-6">
            <RoleCard
              testId="student-role-card"
              onClick={() => startAs("student")}
              icon={GraduationCap}
              accent="from-cyan-500 to-blue-500"
              borderColor="border-b-cyan-500"
              title="Start learning today"
              subtitle="Grade 1 to 12"
              points={["Chapter-organized notes", "Video lessons with progress", "Interactive quizzes & flashcards"]}
              cta="Start learning"
            />
          </motion.div>

          <div className="lg:col-span-12 mt-8 flex items-center gap-8 text-sm text-muted-foreground flex-wrap">
            <Stat n="12" label="Grades" /> <Divider />
            <Stat n="500+" label="Verified tutors" /> <Divider />
            <Stat n="2,400+" label="Notes & videos" /> <Divider />
            <Stat n="47" label="Avg. streak days" />
          </div>
        </div>
        <style>{`
        `}</style>
      </section>

      {/* Feature grid */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-widest font-semibold text-primary mb-3">Every subject, every board</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight max-w-3xl mx-auto">
            One platform. Everything you need to master any subject.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: FileText, title: "Notes hierarchy", desc: "Browse by Class → Subject → Chapter → Topic. Bookmark and download instantly.", color: "from-cyan-500 to-blue-500" },
            { icon: Play, title: "Video learnings", desc: "Auto-resume, watch history, and progress tracking on every lesson.", color: "from-indigo-500 to-purple-500" },
            { icon: Users, title: "Verified tutors", desc: "Filter by subject, class, language, price, and rating. Book a demo in one tap.", color: "from-purple-500 to-pink-500" },
          ].map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="glass card-3d rounded-3xl p-8" data-testid={`feature-${i}`}>
              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-5 shadow-lg`}>
                <f.icon className="w-5 h-5 text-white" strokeWidth={1.8} />
              </div>
              <h3 className="font-display text-xl font-bold mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Streaks / gamification highlight */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="glass rounded-[2.5rem] p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/30 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-secondary/30 blur-3xl" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center relative">
            <div>
              <div className="text-xs uppercase tracking-widest font-semibold text-secondary mb-3">Stay consistent</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-tight">
                Build a habit.<br />Earn badges.<br />Watch progress soar.
              </h2>
              <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">
                Daily streaks, achievement badges, and continue-watching all in one place. Your entire learning journey, beautifully tracked.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <StreakCard icon={Zap} label="Current streak" value="47" unit="days" color="text-orange-500" bg="bg-orange-500/10" />
              <StreakCard icon={Trophy} label="Achievements" value="12" unit="badges" color="text-yellow-500" bg="bg-yellow-500/10" />
              <StreakCard icon={Star} label="Avg. rating" value="4.9" unit="on tutors" color="text-primary" bg="bg-primary/10" />
              <StreakCard icon={Play} label="Watched" value="184" unit="videos" color="text-secondary" bg="bg-secondary/10" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-widest font-semibold text-accent mb-3">Loved worldwide</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight max-w-3xl mx-auto">
            Trusted by students and educators in 40+ cities.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="testimonials-grid">
          {testimonials.map((t) => (
            <div key={t.id} className="glass card-3d rounded-3xl p-8">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map(n => <Star key={n} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />)}
              </div>
              <p className="text-sm text-foreground/90 leading-relaxed mb-6">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                <div>
                  <div className="text-sm font-semibold">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.role} · {t.city}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-24">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-widest font-semibold text-primary mb-3">Everything you need to know</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Frequently asked</h2>
        </div>
        <div className="space-y-3" data-testid="faq-list">
          {faq.map((f, i) => (
            <button key={i} onClick={() => setOpenFaq(openFaq === i ? null : i)}
              data-testid={`faq-${i}`}
              className="w-full text-left glass rounded-2xl p-6 hover:bg-muted/30 transition-colors">
              <div className="flex items-center justify-between gap-4">
                <div className="font-semibold text-base">{f.q}</div>
                <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </div>
              {openFaq === i && (
                <div className="mt-4 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-5xl mx-auto px-6 py-24">
        <div className="relative rounded-[2.5rem] overflow-hidden p-12 md:p-20 text-center glass">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-cyan-500/10" />
          <div className="relative">
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tight">
              Ready to shine brighter?
            </h2>
            <p className="mt-6 text-muted-foreground text-lg max-w-xl mx-auto">
              Join thousands of curious minds learning smarter — one chapter at a time.
            </p>
            <div className="mt-10 flex justify-center gap-3 flex-wrap">
              <Button onClick={() => startAs("student")} data-testid="cta-student"
                className="rounded-full h-14 px-8 bg-primary hover:bg-primary/90 text-primary-foreground shadow-md text-base">
                <Sparkles className="w-4 h-4 mr-2.5" />
                Start learning free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function RoleCard({ testId, onClick, icon: Icon, accent, borderColor, title, subtitle, points, cta }) {
  return (
    <motion.button
      onClick={onClick}
      data-testid={testId}
      whileHover={{ y: -6, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className={`glass rounded-3xl p-8 text-left border-b-4 ${borderColor} group relative overflow-hidden`}
    >
      <div className={`absolute -top-20 -right-20 w-52 h-52 rounded-full bg-gradient-to-br ${accent} opacity-20 blur-2xl group-hover:opacity-40 transition-opacity`} />
      <div className={`relative w-14 h-14 rounded-2xl bg-gradient-to-br ${accent} flex items-center justify-center shadow-lg mb-5`}>
        <Icon className="w-6 h-6 text-white" strokeWidth={1.8} />
      </div>
      <div className="relative text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">{subtitle}</div>
      <div className="relative font-display text-3xl font-black tracking-tight mb-4">{title}</div>
      <ul className="relative space-y-2 mb-6">
        {points.map((p) => (
          <li key={p} className="text-sm text-foreground/70 flex items-center gap-2">
            <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-br ${accent}`} /> {p}
          </li>
        ))}
      </ul>
      <div className="relative flex items-center gap-2 text-sm font-semibold text-primary group-hover:gap-3 transition-all">
        {cta} <ArrowRight className="w-4 h-4" />
      </div>
    </motion.button>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-display text-2xl font-black tracking-tight text-foreground">{n}</div>
      <div className="text-xs text-muted-foreground uppercase tracking-widest mt-0.5">{label}</div>
    </div>
  );
}
function Divider() {
  return <div className="w-px h-10 bg-border hidden md:block" />;
}
function StreakCard({ icon: Icon, label, value, unit, color, bg }) {
  return (
    <div className="glass rounded-2xl p-5">
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center mb-3`}>
        <Icon className={`w-5 h-5 ${color}`} strokeWidth={2} />
      </div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className="font-display text-3xl font-black tracking-tight mt-1">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{unit}</div>
    </div>
  );
}
